import React, { useState, useRef } from 'react';
import {
  Box, Typography, Popover, List, ListItemButton, ListItemText,
  CircularProgress, Divider, IconButton, Tooltip, Switch, LinearProgress,
} from '@mui/material';
import {
  Store as StoreIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Sync as SyncIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { ledger } from '../../theme/modernTheme';
import { useGoogleAdsAccount, GoogleAdsAccount } from '../../contexts/GoogleAdsAccountContext';
import { useTranslation } from 'react-i18next';
import { withPercent } from '../ui/ledger';

const GoogleAdsAccountSelector: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    accounts, activeAccountId, activeAccount, setActiveAccountId,
    syncProgress, triggerSync, toggleSyncEnabled,
  } = useGoogleAdsAccount();
  // Staff see their reseller's accounts but cannot sync or change sync settings
  const isStaff = JSON.parse(localStorage.getItem('user') || '{}').role === 'personel';
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  if (accounts.length === 0) return null;

  // Any account currently syncing (may differ from the active one) — drives the header progress.
  const syncingAccount = accounts.find(a => a.sync_status === 'syncing');

  const L = ledger(theme);
  const dot = (color: string) => <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />;

  const getSyncIndicator = (account: GoogleAdsAccount) => {
    if (account.sync_status === 'syncing') {
      return <CircularProgress size={12} thickness={5} sx={{ color: L.accent }} />;
    }
    if (account.sync_status === 'error') {
      return <ErrorIcon sx={{ fontSize: 14, color: L.loss }} />;
    }
    if (!account.last_synced_at) return dot(L.loss);
    const hoursSince = (Date.now() - new Date(account.last_synced_at).getTime()) / (1000 * 60 * 60);
    if (hoursSince < 24) return dot(L.gain);
    if (hoursSince < 168) return dot(L.alert);
    return dot(L.loss);
  };

  const formatLastSync = (date: string | null) => {
    if (!date) return t('Hiç senkronize edilmedi');
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('Az önce');
    if (mins < 60) return t('{{count}} dakika önce', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('{{count}} saat önce', { count: hours });
    const days = Math.floor(hours / 24);
    return t('{{count}} gün önce', { count: days });
  };

  const displayName = activeAccount?.account_name || t('Tüm Hesaplar');

  const handleSelect = (id: number | null) => {
    setActiveAccountId(id);
    setOpen(false);
  };

  // Sorted: sync_enabled first, then by name
  const sortedAccounts = [...accounts].sort((a, b) => {
    if (a.sync_enabled !== b.sync_enabled) return a.sync_enabled ? -1 : 1;
    return a.account_name.localeCompare(b.account_name);
  });

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={() => setOpen(true)}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          cursor: 'pointer', userSelect: 'none',
          border: `1px solid ${L.ruleStrong}`,
          borderRadius: 1, px: 1.25, height: 34,
          transition: 'background-color 150ms',
          '&:hover': { backgroundColor: L.hover },
        }}
      >
        <StoreIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap', maxWidth: { xs: 96, sm: 160 }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {displayName}
        </Typography>
        {syncingAccount ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={12} thickness={5} sx={{ color: L.accent }} />
            <Typography variant="caption" sx={{ color: L.accent, fontWeight: 500, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {withPercent(String(syncProgress[syncingAccount.id]?.percent ?? 0))}
            </Typography>
          </Box>
        ) : (activeAccount && getSyncIndicator(activeAccount))}
        <ArrowDropDownIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { elevation: 8, sx: { mt: 0.5, minWidth: 320, maxHeight: 480 } } }}
      >
        <List dense disablePadding>
          {/* All accounts option */}
          <ListItemButton
            selected={activeAccountId === null}
            onClick={() => handleSelect(null)}
            sx={{ py: 1.25 }}
          >
            <StoreIcon sx={{ fontSize: 18, mr: 1.5, color: 'text.secondary' }} />
            <ListItemText
              primary={t('Tüm Hesaplar')}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: activeAccountId === null ? 600 : 400 }}
            />
            {activeAccountId === null && <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />}
          </ListItemButton>
          <Divider />

          {/* Individual accounts */}
          {sortedAccounts.map(account => (
            <ListItemButton
              key={account.id}
              selected={activeAccountId === account.id}
              onClick={() => handleSelect(account.id)}
              sx={{
                py: 1, px: 2, position: 'relative',
                // Dim only accounts that have never been synced (no data yet) — a synced account
                // stays fully visible even when auto-sync is off, since manual sync is the norm.
                opacity: account.last_synced_at ? 1 : 0.6,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5 }}>
                {getSyncIndicator(account)}
              </Box>
              <ListItemText
                primary={account.account_name}
                secondary={account.sync_status === 'syncing'
                  ? t('Senkronize ediliyor... {{percent}}', { percent: withPercent(String(syncProgress[account.id]?.percent ?? 0)) }) + (syncProgress[account.id]?.label ? ' · ' + syncProgress[account.id].label : '')
                  : formatLastSync(account.last_synced_at)}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: activeAccountId === account.id ? 600 : 400, noWrap: true }}
                secondaryTypographyProps={{ fontSize: '0.7rem', color: account.sync_status === 'syncing' ? L.accent : 'text.secondary' }}
              />
              {account.sync_status === 'syncing' && (
                <LinearProgress
                  variant="determinate"
                  value={syncProgress[account.id]?.percent ?? 0}
                  sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, borderRadius: 0 }}
                />
              )}
              {!isStaff && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                {/* Sync button */}
                {account.sync_status !== 'syncing' && (
                  <Tooltip title={t('Senkronize et')}>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); triggerSync(account.id); }}
                      sx={{ p: 0.5 }}
                    >
                      <SyncIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {/* Auto-sync toggle */}
                <Tooltip title={account.sync_enabled ? t('Otomatik senkronizasyon aktif') : t('Otomatik senkronizasyon pasif')}>
                  <Switch
                    size="small"
                    checked={account.sync_enabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSyncEnabled(account.id)}
                    sx={{ ml: 0.5 }}
                  />
                </Tooltip>
              </Box>
              )}
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default GoogleAdsAccountSelector;
